import { useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useTenantLists, useUpdateTenantList } from "@/hooks/useTenantLists";
import { useUnits } from "@/hooks/useUnits";
import { getApiErrorMessage } from "@/lib/api/api";
import type { UnitFamily } from "@/types/unit";

interface UnitSettingsCardProps {
  canManage: boolean;
}

/**
 * Settings → Configuration → Units of measure (T-1006).
 *
 * A kitchen chooses which units it works in; it does not get to say what they
 * are. A pound is 453.59237 grams whoever is asking, and costing divides by
 * that — so this narrows the pickers rather than defining anything. Turning a
 * unit off does not disturb an ingredient already saved in it: the picker keeps
 * offering a stored value it would otherwise hide.
 */
export function UnitSettingsCard({ canManage }: UnitSettingsCardProps) {
  const { t } = useLingui();
  const { data, isLoading, isError } = useTenantLists();
  // Unfiltered on purpose: this is the card that decides the filter, so it has
  // to see everything, including what the tenant has just switched off.
  const { groups } = useUnits(true, "all");
  const save = useUpdateTenantList();

  const familyLabels: Record<UnitFamily, string> = {
    weight: t`Weight`,
    volume: t`Volume`,
    count: t`Count`,
    container: t`Bought by the container`,
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Units of measure`}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Units of measure`}</CardTitle>
          <CardDescription>{t`The unit list could not be loaded.`}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const chosen = new Set(data.lists.units);

  const toggle = (unit: string, on: boolean) => {
    // Sent in catalog order rather than click order: a unit list has no
    // meaningful ordering of its own, and reshuffling a picker between visits
    // is how someone picks the wrong one.
    const next = data.availableUnits.filter((u) => (u === unit ? on : chosen.has(u)));
    save.mutate(
      { kind: "units", values: next },
      {
        onSuccess: () => toast.success(t`Units of measure saved`),
        onError: (err) => toast.error(getApiErrorMessage(err, t`Failed to save the unit list`)),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t`Units of measure`}</CardTitle>
        <CardDescription>
          {t`Choose the units this kitchen works in. What a unit converts to is fixed — this only decides which ones are offered.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {groups.map((group) => (
          <div key={group.family} className="flex flex-col gap-2">
            <p className="text-sm font-medium text-(--color-text-primary)">
              {familyLabels[group.family]}
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {group.units.map((unit) => (
                <div key={unit} className="flex items-center gap-2">
                  <Checkbox
                    id={`unit-${unit}`}
                    checked={chosen.has(unit)}
                    disabled={!canManage || save.isPending}
                    onCheckedChange={(state) => toggle(unit, state === true)}
                  />
                  <Label htmlFor={`unit-${unit}`} className="cursor-pointer">
                    {unit}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        ))}

        {chosen.size === 0 && (
          <p className="text-sm text-(--color-text-secondary)">
            {t`No units are switched on, so every unit picker will be empty.`}
          </p>
        )}

        {save.isPending && (
          <p className="flex items-center gap-1.5 text-sm text-(--color-text-secondary)">
            <Loader2 className="size-3.5 animate-spin" />
            {t`Saving…`}
          </p>
        )}

        {canManage && (
          <div>
            <Button
              variant="ghost"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  { kind: "units", values: [...data.defaults.units] },
                  {
                    onSuccess: () => toast.success(t`Every unit switched back on`),
                    onError: (err) =>
                      toast.error(getApiErrorMessage(err, t`Failed to save the unit list`)),
                  },
                )
              }
            >
              {t`Switch every unit back on`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
